import React, { lazy, Suspense, useEffect } from "react";
import { slugify } from "common-ui-components/components/utils/string-utils";
import { useNavigate } from 'react-router-dom';
import { redirectToPage } from "app/common/redirects/Redirects";
import { isLab, isLocalhost, PRODUCT_CONTEXT } from "../../../common-ui-components/components/utils/Config";
import { normalizeMSPProductKeys } from "ProductConfig";

// importing icons
import UserAdministrationIcon from "./icons/userAdministration.svg";
import RolesIcon from "./icons/roles.svg";
import GroupsIcon from "./icons/groups.svg";
import RebrandingIcon from "./icons/rebranding.svg";
import DeploymentIcon from "./icons/deployment.svg";
import DomainIcon from "./icons/domain.svg";
import ProxySettingsIcon from "./icons/proxySettings.svg";
import ScheduleScanIcon from "./icons/scheduleScan.svg";
import ActionLogViewerIcon from "./icons/actionLogViewer.svg";
import AuthenticationIcon from "./icons/authentication.svg";
import ToolSettingsIcon from "./icons/toolSettings.svg";
import RemoteControlSettingsIcon from "./icons/remoteControlSettings.svg";
import SubscriptionIcon from "./icons/subscription.svg";
import OfferIcon from "./icons/offer.svg";
import organization from "./icons/organization.svg";
import enrollment from "./icons/enrollment.svg";
import inventory from "./icons/inventory.svg";
import secuirty from "./icons/security.svg";
import accounts from "./icons/account.svg";
import toolsIcon from "./icons/tools.svg";
import patchIcon from "./icons/patch.svg";
import { browserIdleCallback } from "utils";
import ResponseLoader from "common-ui-components/components/ResponseLoader/ResponseLoader";

// enrollment
const loadDevices = () => import("../../enrollment-new/devices/Devices");
const loadDomains = () => import("../../enrollment-new/domains/Domains");
const loadConnectors = () => import("../../enrollment-new/connectors/Connectors");
const Devices = lazy(loadDevices);
const Domains = lazy(loadDomains);
const Connectors = lazy(loadConnectors);

// inventory
const loadGeoLocation = () => import("../../inventory-new/geo-location/GeoLocation");
const loadSoftwareCategory = () => import("app/inventory-new/software-category/SoftwareCategory");
const loadScheduleScan = () => import("../../inventory-new/schedule-scan/ScheduleScan");
const loadScanSettings = () => import("app/inventory-new/scan-settings/ScanSetings");
const loadDeviceWarranty = () => import("app/inventory-new/device-warranty/DeviceWarranty");
const GeoLocation = lazy(loadGeoLocation);
const SoftwareCategory = lazy(loadSoftwareCategory);
const ScheduleScan = lazy(loadScheduleScan);
const ScanSettings = lazy(loadScanSettings);
const DeviceWarranty = lazy(loadDeviceWarranty);

// remote
const loadSessionConfig = () => import("../../remote/settings/user-confirmation/UserConfirmation");
const loadConfigLan = () => import("../../remote/settings/configure LAN/ConfigureWOL");
const SessionConfig = lazy(loadSessionConfig);
const ConfigLan = lazy(loadConfigLan);

// organization
const loadCustomerPage = () => import("../organization/customers/Customers");
const loadOrgUsersPage = () => import("../organization/users/OrgUsersPage");
const loadRoles = () => import("../organization/roles/Roles");
const loadGroupsPage = () => import("../organization/groups/GroupsPage");
const loadActivityLogs = () => import("../organization/activity log/ActivtyLog");
const loadRebranding = () => import("../organization/Rebranding/Rebranding");
const loadCredentialManager = () => import("../organization/security/Credential Manager/CredentialManager");
const loadCustomFields = () => import("../organization/CustomFields/CustomFields");
const loadCustomData = () => import("../organization/CustomData/CustomData");
const loadSelfService=()=>import("../organization/self-service/SelfService");
const OrgCustomerPage = lazy(loadCustomerPage);
const OrgUsersPage = lazy(loadOrgUsersPage);
const Roles = lazy(loadRoles);
const GroupsPage = lazy(loadGroupsPage);
const ActivityLogs = lazy(loadActivityLogs);
const Rebranding = lazy(loadRebranding);
const CredentialManager = lazy(loadCredentialManager);
const CustomFields = lazy(loadCustomFields);
const CustomData = lazy(loadCustomData);
const SelfService=lazy(loadSelfService)

// deployment in the enrollment
const loadAzure = () => import("../../enrollment-new/cloud-deployment/azure/Azure");
const loadGPO = () => import("../../enrollment-new/cloud-deployment/gpo/GPO");
const loadIntune = () => import("../../enrollment-new/cloud-deployment/Intune/Intune");
const loadSCCM = () => import("../../enrollment-new/cloud-deployment/SCCM/SCCM");
const Azure = lazy(loadAzure);
const GPO = lazy(loadGPO);
const Intune = lazy(loadIntune);
const SCCM = lazy(loadSCCM);

// security
const loadAuthentication = () => import("../organization/security/authentication/Authentication");
const loadSamlAuthentication = () => import("../organization/security/saml-authentication/SamlAuthentication");
const loadSetupAuthenticatorApp = () => import("../organization/security/authentication/Setupauthapp");
const loadSessionSettings = () => import("../organization/security/session-settings/SessionSettings");
const loadAgentProtection = () => import("../organization/security/agent protection/AgentProtection");
const loadDatacleanup = () => import("../organization/security/data cleanup/Datacleanup");
const loadIPRestriction = () => import("../organization/security/IP restriction/IPRestriction");
const loadGovernanceHub = () => import("../organization/security/privacy/PrivacyComplianceHub");

const GovernancePrivacyComplianceHub = lazy(loadGovernanceHub);
const Authentication = lazy(loadAuthentication);
const SetupAuthenticatorApp = lazy(loadSetupAuthenticatorApp);
const SessionSettings = lazy(loadSessionSettings);
const SamlAuthentication = lazy(loadSamlAuthentication);
const AgentProtection = lazy(loadAgentProtection);
const Datacleanup = lazy(loadDatacleanup);
const IPRestriction = lazy(loadIPRestriction);

// patch
const loadBandwidthOptimization = () => import("app/patch/settings/BandwidthOptimization");
const loadPatchDb = () => import("app/patch/view/patch-db/PatchDb");
const BandwidthOptimization = lazy(loadBandwidthOptimization);
const PatchDb = lazy(loadPatchDb);

// Accounts
const loadSubscription = () => import("../organization/subscription/Subscription");
const loadOffers = () => import("../organization/offers/Offers");
const loadPersonalization = () => import("../Personalization/Personalization");
const Subscription = lazy(loadSubscription);
const Offers = lazy(loadOffers);
const Personalization = lazy(loadPersonalization);

const withSuspense = (ComponentElement) => (
  <Suspense
    fallback={
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          width: "100%",
          padding: "2rem",
        }}
      >
        <ResponseLoader />
      </div>
    }
  >
    {ComponentElement}
  </Suspense>
);

export const SettingsApiExplorer = () => {
  const history = useNavigate();

  useEffect(() => {
    redirectToPage(history, "/app/api-explorer");
  }, [history]);

  return null;
};

const loadApiExplorer = () => Promise.resolve();

const SettingsApiExplorerComponent = lazy(() =>
  Promise.resolve({
    default: SettingsApiExplorer,
  })
);

console.log(isLocalhost(),"<-- localhost")
console.log(isLab(),"<-- lab")

// --- NAVIGATION CONFIGURATION ---

const NavigationList = [
  {
    groupTitle: "Organization",
    groupIcon: organization,
    product: ["am", "em", "ra","vm","pm"],
    groupNavList: [
      {
        title: "Customers",
        icon: UserAdministrationIcon,
        url: "/customers/*",
        component: withSuspense(<OrgCustomerPage />),
        prefetch: loadCustomerPage,
        key: "CUSTOMERS",
        product: ["em-msp"],
      },
      {
        title: "Users",
        extendedTitle: "Users",
        icon: UserAdministrationIcon,
        url: "/users/*",
        component: withSuspense(<OrgUsersPage />),
        prefetch: loadOrgUsersPage,
        key: "USER_MANAGEMENT",
        product: ["am", "em", "ra","vm","pm"],
      },
      {
        title: "Roles",
        icon: RolesIcon,
        url: "/roles/*",
        component: withSuspense(<Roles />),
        prefetch: loadRoles,
        key: "ROLE",
        product: ["am", "em", "ra","vm","pm"],
      },
      {
        title: "Groups",
        icon: GroupsIcon,
        url: "/groups/*",
        component: withSuspense(<GroupsPage />),
        prefetch: loadGroupsPage,
        key: "GROUPS",
        product: ["am", "em", "ra","vm","pm"],
      },
      {
        title: "Company Details",
        icon: RebrandingIcon,
        url: "/organization-settings/*",
        component: withSuspense(<Rebranding />),
        prefetch: loadRebranding,
        key: "REBRANDING",
        product: ["am", "em", "ra","vm","pm"],
      },
      {
        title: "Credential Manager",
        icon: ActionLogViewerIcon,
        url: "/credential-manager/*",
        component: withSuspense(<CredentialManager />),
        prefetch: loadCredentialManager,
        key: "CREDENTIAL_MANAGER",
        product: ["em"],
      },
      {
        title: "Custom Fields",
        icon: ActionLogViewerIcon,
        url: "/custom-fields/*",
        component: withSuspense(<CustomFields />),
        prefetch: loadCustomFields,
        key: "CUSTOM_FIELDS",
        product: ["em", "am", "ra","vm","pm"],
      },
      {
        title: "Custom Data",
        icon: ActionLogViewerIcon,
        url: "/custom-data/*",
        component: withSuspense(<CustomData />),
        prefetch: loadCustomData,
        key: "CUSTOM_DATA",
        product: ["em", "am", "ra","vm","pm"],
      },
      (isLocalhost() || isLab()) && {
        title:"Self Service Portal",
        url:"/self-service/*",
        component:withSuspense(<SelfService/>),
        key:"SELF_SERVICE",
        product:["em"]
      },
      {
        title: "API Explorer",
        url: "/api-explorer/*",
        key: "API_EXPLORER",
        component: withSuspense(<SettingsApiExplorerComponent />),
        prefetch: loadApiExplorer,
        product: ["em", "am", "ra","vm"],
      },
    ],
  },
  {
    groupTitle: "Enrollment",
    groupIcon: enrollment,
    product: ["am", "em", "ra","vm","pm"],
    groupNavList: [
      {
        title: "Devices",
        icon: DeploymentIcon,
        url: "/devices/*",
        component: withSuspense(<Devices settingURL={true} />),
        prefetch: loadDevices,
        key: "DEPLOYMENT",
        product: ["am", "em", "ra","vm","pm"],
      },
      {
        title: "Domain",
        icon: DomainIcon,
        url: "/domain/*",
        component: withSuspense(<Domains />),
        prefetch: loadDomains,
        key: "DOMAIN",
        product: ["am", "em", "ra","vm","pm"],
      },
      {
        title: "Connector",
        icon: ProxySettingsIcon,
        url: "/connector/*",
        component: withSuspense(<Connectors />),
        prefetch: loadConnectors,
        key: "CONNECTOR",
        product: ["am", "em", "ra","vm","pm"],
      },
      {
        title: "Microsoft Azure",
        url: "/azure/*",
        component: withSuspense(<Azure />),
        prefetch: loadAzure,
        key: "BULK_DEPLOYMENT_AZURE",
        product: ["am", "em", "ra","vm","pm"],
      },
      {
        title: "Active Directory GPO",
        url: "/gpo/*",
        component: withSuspense(<GPO />),
        prefetch: loadGPO,
        key: "BULK_DEPLOYMENT_GPO",
        product: ["am", "em", "ra","vm","pm"],
      },
      {
        title: "Microsoft Intune",
        url: "/intune/*",
        component: withSuspense(<Intune />),
        prefetch: loadIntune,
        key: "BULK_DEPLOYMENT_INTUNE",
        product: ["am", "em", "ra","vm","pm"],
      },
      {
        title: "SCCM",
        url: "/sccm/*",
        component: withSuspense(<SCCM />),
        prefetch: loadSCCM,
        key: "BULK_DEPLOYMENT_SCCM",
        product: ["am", "em", "ra","vm","pm"],
      },
    ],
  },
  {
    groupTitle: "Security",
    groupIcon: secuirty,
    product: ["am", "em", "ra","vm","pm"],
    groupNavList: [
      {
        title: "Activity Log ",
        icon: ActionLogViewerIcon,
        url: "/activity-log/*",
        component: withSuspense(<ActivityLogs />),
        prefetch: loadActivityLogs,
        key: "ACTIVITY",
        product: ["am", "em", "ra","vm","pm"],
      },
      {
        title: "MFA",
        icon: AuthenticationIcon,
        url: "/authentication/*",
        component: withSuspense(<Authentication />),
        prefetch: loadAuthentication,
        key: "TWO_FA_SETTINGS",
        product: ["am", "em", "ra","vm","pm"],
        subPages: [
          {
            title: "Setup Authenticator App",
            url: "/authentication/setup-authenticator-app",
            component: withSuspense(<SetupAuthenticatorApp />),
            prefetch: loadSetupAuthenticatorApp,
            key: "SETUP_AUTHENTICATOR_APP",
            product: ["am", "em", "ra","vm","pm"],
          },
        ],
      },
      {
        title: "SAML Authentication",
        icon: AuthenticationIcon,
        url: "/saml-authentication",
        component: withSuspense(<SamlAuthentication />),
        prefetch: loadSamlAuthentication,
        key: "SAML_AUTHENTICATION",
        product: ["am", "em", "ra","vm","pm"],
      },
      {
        title: "IP restriction",
        url: "/ip-restriction/*",
        component: withSuspense(<IPRestriction />),
        prefetch: loadIPRestriction,
        key: "IP_RESTRICTION",
        product: ["am", "em", "ra", "vm","pm"],
      },
      {
        title: "Session Settings",
        url: "/session-settings/*",
        component: withSuspense(<SessionSettings />),
        prefetch: loadSessionSettings,
        key: "SESSION_SETTINGS",
        product: ["am", "em", "ra","vm","pm"],
      },
      {
        title: "Agent Protection",
        url: "/agent-protection/*",
        component: withSuspense(<AgentProtection />),
        prefetch: loadAgentProtection,
        key: "AGENT_PROTECTION",
        product: ["am", "em", "ra","vm","pm"],
      },
      {
        title: "Data Cleanup",
        url: "/data-cleanup/*",
        component: withSuspense(<Datacleanup />),
        prefetch: loadDatacleanup,
        key: "DATA_CLEANUP_SETTINGS",
        product: ["am", "em"],
      },
      {
        title: "Privacy & Compliance",
        url: "/privacy-compliance/*",
        component: withSuspense(<GovernancePrivacyComplianceHub />),
        prefetch: loadGovernanceHub,
        key: "PRIVACY_COMPLIANCE",
        product: ["am", "em", "ra","vm","pm"],
      },
    ],
  },
  {
    groupTitle: "Inventory",
    groupIcon: inventory,
    product: ["am", "em"],
    groupNavList: [
      {
        title: "Schedule Scan",
        icon: ScheduleScanIcon,
        url: "/schedule-scan/*",
        component: withSuspense(<ScheduleScan />),
        prefetch: loadScheduleScan,
        key: "SCHEDULE_SCAN",
        product: ["am", "em"],
      },
      {
        title: "Software Category",
        url: "/software-category/*",
        component: withSuspense(<SoftwareCategory />),
        prefetch: loadSoftwareCategory,
        key: "SOFTWARE_CATEGORY",
        product: ["am", "em"],
      },
      {
        title: "Scan Settings",
        url: "/scan-settings/*",
        key: "SCAN_SETTINGS",
        component: withSuspense(<ScanSettings />),
        prefetch: loadScanSettings,
        product: ["am", "em"],
      },
      {
        title: "Geo Location",
        url: "/geo-location/*",
        component: withSuspense(<GeoLocation settings={true} />),
        prefetch: loadGeoLocation,
        key: "GEO_LOCATION",
        product: ["am", "em"],
      },
      {
        title: "Device Warranty",
        url: "/device-warranty/*",
        component: withSuspense(<DeviceWarranty />),
        prefetch: loadDeviceWarranty,
        key: "DEVICE_WARRANTY",
        product: ["am", "em"],
      },
    ],
  },
  {
    groupTitle: "Remote",
    groupIcon: toolsIcon,
    product: ["em", "ra"],
    groupNavList: [
      {
        title: "Configure WoL",
        icon: ToolSettingsIcon,
        url: "/configure-wol/*",
        component: withSuspense(<ConfigLan />),
        prefetch: loadConfigLan,
        product: ["em", "ra"],
        key: "CONFIGURE_WOL",
      },
      {
        title: "User Confirmation",
        icon: RemoteControlSettingsIcon,
        url: "/user-confirmation/*",
        component: withSuspense(<SessionConfig />),
        prefetch: loadSessionConfig,
        product: ["em", "ra"],
        key: "USER_CONFIRMATION",
      },
    ],
  },
  {
    groupTitle: "Patch",
    groupIcon: patchIcon,
    product: ["em", "vm","pm"],
    groupNavList: [
      {
        title: "Bandwidth Optimization",
        url: "/bandwidth-optimization/*",
        component: withSuspense(<BandwidthOptimization />),
        prefetch: loadBandwidthOptimization,
        key: "BANDWIDTH_OPTIMIZATION",
        product: ["em", "vm","pm"],
      },
      {
        title: "DB Sync",
        url: "/db-sync/*",
        component: withSuspense(<PatchDb />),
        prefetch: loadPatchDb,
        key: "DB_SYNC",
        product: ["em", "vm","pm"],
      },
    ],
  },
  {
    groupTitle: "Account",
    groupIcon: accounts,
    product: ["am", "em", "ra","vm","pm"],
    groupNavList: [
      {
        title: "Personalization",
        icon: SubscriptionIcon,
        url: "/personalization/*",
        key: "PERSONALIZATION",
        component: withSuspense(<Personalization />),
        prefetch: loadPersonalization,
        product: ["em", "am", "ra","vm","pm"],
      },
      // Subscription was blocked for MSP products. Keep this commented to allow it in settings.
      // !isMspProduct() &&
      {
        title: "Subscription",
        icon: SubscriptionIcon,
        url: "/subscription/*",
        component: withSuspense(<Subscription />),
        prefetch: loadSubscription,
        key: "SUBSCRIPTIONS",
        product: ["am", "em", "ra","vm","pm"],
      },
      {
        title: "Offer",
        icon: OfferIcon,
        url: "/offer/*",
        component: withSuspense(<Offers />),
        prefetch: loadOffers,
        key: "OFFERS",
        product: ["am", "em", "ra","vm","pm"],
      },
    ],
  },
];

const NormalizedNavigationList = NavigationList.map((group) => ({
  ...group,
  product: normalizeMSPProductKeys(group.product),
  groupNavList: group.groupNavList.map((item) => ({
    ...item,
    product: normalizeMSPProductKeys(item.product),
  })),
}));

const newFeatures = ["CUSTOM_DATA", "CUSTOM_FIELDS"];

// --- PRODUCT WISE SPLIT ---
export const SettingsNewNavList = NormalizedNavigationList.filter((group) =>
  group.product.includes(PRODUCT_CONTEXT)
)
  .map((group) => {
    const filteredItems = group.groupNavList.filter(
      (item) => !item.product || item.product.includes(PRODUCT_CONTEXT)
    );

    if (!filteredItems.length) return null;

    return {
      label: group.groupTitle,
      icon: group.groupIcon,
      items: filteredItems.map((item) => {
        const fullPath = `/app/settings/${slugify(group.groupTitle)}${item.url}`;
        return {
          label: item.extendedTitle || item.title,
          url: fullPath.replace(/\/\*$/, ""),
          path: fullPath,
          key: item.key,
          newFeature: newFeatures.includes(item.key),
          product: item.product,
          component: item.component,
          prefetch: item.prefetch,
          ...(item.extendedTitle && { extendedTitle: item.extendedTitle }),
        };
      }),
    };
  })
  .filter(Boolean);

const isRouteActive = (pathname, url) =>
  pathname === url || pathname.startsWith(`${url}/`);

export const prefetchSettingsSiblingRoutes = (navList, pathname) => {
  const activeGroup = navList.find((group) =>
    group.items?.some((item) => isRouteActive(pathname, item.url))
  );

  if (!activeGroup) return;

  const activeItem = activeGroup.items.find((item) =>
    isRouteActive(pathname, item.url)
  );

  if (!activeItem) return;

  const prefetch = () => {
    activeGroup.items.forEach((item) => {
      if (item.url === activeItem.url || typeof item.prefetch !== "function") {
        return;
      }

      item.prefetch();
    });
  };

  browserIdleCallback(prefetch);
};